import * as Sentry from "@sentry/browser";
import { createContext, type PropsWithChildren, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useValidateToken } from "../hooks/authHooks";
import type { CurrentUser } from "../models/user";

type UserContextType = {
  currentUser: CurrentUser | undefined;
  isValidating: boolean;
  username: string;
};

export const UserContext = createContext<UserContextType>({
  currentUser: undefined,
  isValidating: false,
  username: "",
});

export const UserProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [username, setUsername] = useLocalStorage<string>("username", "");

  const { data, isLoading: isValidating, isError } = useValidateToken();
  const currentUser = !isError ? data?.user : undefined;

  useEffect(() => {
    if (currentUser) {
      Sentry.setUser({
        username: currentUser.name,
        id: currentUser.id?.toString(),
        email: currentUser.emailAddress ?? undefined,
      });
      setUsername(currentUser.name);
    } else {
      Sentry.setUser(null);
    }
  }, [currentUser, setUsername]);

  const context = {
    currentUser,
    username,
    isValidating,
  };

  return (
    <UserContext.Provider value={context}>{children}</UserContext.Provider>
  );
};
